import { NectarTasksAllDone } from '@/features/nectar/components/NectarTasksAllDone'
import { formValuesToUserProfile, useProfile, userProfileToFormValues } from '@/features/profile/api/profile'
import { ProfileDetailsForm, ProfileDetailsFormValues } from '@/features/profile/ProfileDetailsForm'
import { isCaptchaFailedError, useTurnstileCaptcha } from '@/features/turnstile'
import { Spinner } from '@/ui-kit'
import { t } from '@lingui/core/macro'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useSubmitMissionProfile } from '../api'
import { Mission } from '../api/models'
import { useMissionsUrl } from '../context/MissionsUrlProvider'
import { useMissionRedirect } from '../hooks/useMissionRedirect'

type MissionDemographicsFormProps = {
  mission: Mission
}

export const MissionDemographicsForm = ({ mission }: MissionDemographicsFormProps) => {
  const { missionsUrl } = useMissionsUrl()
  const { redirect } = useMissionRedirect()

  const navigate = useNavigate()
  const { data: profile, isLoading } = useProfile()
  const { mutateAsync: submitMissionProfile, isPending: isSubmittingProfile } = useSubmitMissionProfile()
  const captcha = useTurnstileCaptcha({ enabled: true, action: 'profile-mission-submit' })

  const onSubmit = async (data: ProfileDetailsFormValues) => {
    try {
      await submitMissionProfile({
        missionId: mission.id,
        data: formValuesToUserProfile(data),
        turnstileToken: captcha.token ?? undefined
      })
      captcha.reset()
      navigate(redirect ?? missionsUrl)
      toast.success(t`Mission submitted successfully`)
    } catch (error) {
      captcha.reset()
      console.error(error)
      if (isCaptchaFailedError(error)) {
        toast.error(t`Verification failed. Please try again.`)
      } else {
        toast.error(t`Failed to submit mission`)
      }
    }
  }

  return isLoading ? (
    <div className="flex justify-center py-12">
      <Spinner />
    </div>
  ) : mission.view.nextSubmissionAvailable ? (
    <div className="max-w-[900px]">
      <ProfileDetailsForm
        onSubmit={onSubmit}
        defaultValues={profile ? userProfileToFormValues(profile) : undefined}
        isSubmittingProfile={isSubmittingProfile}
        captchaSlot={captcha.renderWidget('max-w-[300px]')}
        submitDisabled={captcha.active && !captcha.token}
      />
    </div>
  ) : (
    <div className="m-auto">
      <NectarTasksAllDone name={mission.title} />
    </div>
  )
}
